import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row, Col, TabContent, TabPane } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';

//import images
import bell from "../../assets/images/svg/bell.svg";

//SimpleBar
import SimpleBar from "simplebar-react";

// Redux - lấy thông báo từ API thật (system-api/Notification)
import {
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markAllNotificationsRead,
} from '../../slices/notifications/thunk';
import { NotificationItem } from '../../slices/notifications/types';
import { getNotificationTypeMeta, formatRelativeTime, isUrgentPriority, isHighPriority } from './Notification/notificationMeta';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_notification_dropdown');

type TabKey = 'all' | 'urgent' | 'high';

const NotificationDropdown = () => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    const { items, unreadCount, loading } = useSelector((state: any) => state.Notifications);

    //Dropdown Toggle
    const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
    };

    //Tab
    const [activeTab, setActiveTab] = useState<TabKey>('all');
    const toggleTab = (tab: TabKey) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    useEffect(() => {
        dispatch(fetchNotifications());
        dispatch(fetchUnreadCount());

        // Polling: tự động gọi lại API mỗi 3 giây để cập nhật thông báo mới
        // (chưa có SignalR/real-time nên dùng polling tạm thời)
        // const intervalId = setInterval(() => {
        //     dispatch(fetchNotifications());
        //     dispatch(fetchUnreadCount());
        // }, 3000);

        // return () => clearInterval(intervalId);
    }, [dispatch]);

    const urgentItems = useMemo(
        () => items.filter((item: NotificationItem) => isUrgentPriority(item.priority)),
        [items]
    );
    const highItems = useMemo(
        () => items.filter((item: NotificationItem) => isHighPriority(item.priority)),
        [items]
    );

    const handleItemClick = (item: NotificationItem) => {
        if (!item.isRead) {
            dispatch(markNotificationRead(item.id));
        }
        // Chỉ đóng dropdown khi thực sự điều hướng sang trang khác;
        // đánh dấu đã đọc đơn thuần thì giữ dropdown mở để xem tiếp các thông báo khác.
        if (item.link) {
            setIsNotificationDropdown(false);
            navigate(item.link);
        }
    };

    const handleMarkAllRead = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (unreadCount > 0) {
            dispatch(markAllNotificationsRead());
        }
    };

    const handleViewAll = () => {
        setIsNotificationDropdown(false);
        navigate('/notifications');
    };

    const renderList = (list: NotificationItem[]) => {
        if (loading && items.length === 0) {
            return (
                <div className="p-3">
                    {[...Array(3)].map((_, idx) => (
                        <div className="placeholder-glow mb-3" key={idx}>
                            <span className="placeholder col-4 d-block mb-1"></span>
                            <span className="placeholder col-9 d-block"></span>
                        </div>
                    ))}
                </div>
            );
        }

        if (list.length === 0) {
            return (
                <div className="p-4">
                    <div className="w-25 w-sm-50 pt-3 mx-auto">
                        <img src={bell} className="img-fluid" alt={st('khong_co_thong_bao_alt')} />
                    </div>
                    <div className="text-center pb-4 mt-2">
                        <h6 className="fs-15 fw-semibold lh-base">{st('khong_co_thong_bao_nao_title')}</h6>
                    </div>
                </div>
            );
        }

        return (
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                {list.map((item) => {
                    const meta = getNotificationTypeMeta(item.type);
                    return (
                        <div
                            key={item.id}
                            className={classnames('text-reset notification-item d-block dropdown-item position-relative', { active: !item.isRead })}
                            role="button"
                            tabIndex={0}
                            aria-label={!item.isRead ? st('title_chua_doc_text', { title: item.title }) : item.title}
                            onClick={() => handleItemClick(item)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleItemClick(item);
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: !item.isRead ? '#eff6ff' : 'transparent',
                                borderLeft: !item.isRead ? '3px solid #2563eb' : '3px solid transparent',
                            }}
                        >
                            <div className="d-flex">
                                <div className="avatar-xs me-3" style={{ opacity: !item.isRead ? 1 : 0.55 }}>
                                    <span
                                        className="avatar-title rounded-circle fs-16"
                                        style={{ backgroundColor: meta.iconBg, color: meta.iconColor }}
                                    >
                                        <i className={meta.icon}></i>
                                    </span>
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className={classnames('mt-0 mb-1 fs-13 lh-base', { 'fw-semibold': !item.isRead, 'fw-normal text-muted': item.isRead })}>
                                        {item.title}
                                        {!item.isRead && <span className="visually-hidden"> {st('chua_doc_text')}</span>}
                                    </h6>
                                    <p
                                        className={classnames('mb-1 fs-12', item.isRead ? 'text-muted' : 'text-body')}
                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                    >
                                        {item.content}
                                    </p>
                                    <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                        <span><i className="mdi mdi-clock-outline"></i> {formatRelativeTime(item.createdAt)}</span>
                                        {item.sender && <span className="ms-2">· {item.sender}</span>}
                                    </p>
                                </div>
                                {!item.isRead && (
                                    <div className="px-1 pt-1">
                                        <span
                                            className="d-inline-block rounded-circle"
                                            style={{ width: 8, height: 8, backgroundColor: '#2563eb' }}
                                            aria-hidden="true"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </SimpleBar>
        );
    };

    return (
        <React.Fragment>
            <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
                    <i className='bx bx-bell fs-22'></i>
                    {unreadCount > 0 && (
                        <span
                            className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">{unreadCount > 99 ? '99+' : unreadCount}<span
                                className="visually-hidden">{st('thong_bao_chua_doc_text')}</span></span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                        <div className="p-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white"> {st('thong_bao_title')} </h6>
                                </Col>
                                <div className="col-auto dropdown-tabs">
                                    {unreadCount > 0 ? (
                                        <a
                                            href="#!"
                                            className="badge bg-light-subtle fs-13 text-body text-decoration-none"
                                            onClick={handleMarkAllRead}
                                            title={st('danh_dau_tat_ca_da_doc_title')}
                                        >
                                            {unreadCount} {st('moi_button')}
                                        </a>
                                    ) : (
                                        <span className="badge bg-light-subtle fs-13 text-body">{st('0_moi_text')}</span>
                                    )}
                                </div>
                            </Row>
                        </div>

                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom flex-nowrap">
                                <NavItem className="text-nowrap">
                                    <NavLink
                                        href="#"
                                        className={classnames('px-2', { active: activeTab === 'all' })}
                                        onClick={() => { toggleTab('all'); }}
                                    >
                                        {st('tat_ca_text')}{items.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem className="text-nowrap">
                                    <NavLink
                                        href="#"
                                        className={classnames('px-2', { active: activeTab === 'urgent' })}
                                        onClick={() => { toggleTab('urgent'); }}
                                    >
                                        {st('khan_cap_text')}{urgentItems.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem className="text-nowrap">
                                    <NavLink
                                        href="#"
                                        className={classnames('px-2', { active: activeTab === 'high' })}
                                        onClick={() => { toggleTab('high'); }}
                                    >
                                        {st('uu_tien_text')}{highItems.length})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>

                    </div>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="all" className="py-2 ps-2">
                            {renderList(items)}
                            <div className="my-3 text-center">
                                <button type="button" className="btn btn-soft-success waves-effect waves-light" onClick={handleViewAll}>
                                    {st('xem_tat_ca_thong_bao_button')} <i className="ri-arrow-right-line align-middle"></i>
                                </button>
                            </div>
                        </TabPane>

                        <TabPane tabId="urgent" className="py-2 ps-2">
                            {renderList(urgentItems)}
                            <div className="my-3 text-center">
                                <button type="button" className="btn btn-soft-success waves-effect waves-light" onClick={handleViewAll}>
                                    {st('xem_tat_ca_thong_bao_button')} <i className="ri-arrow-right-line align-middle"></i>
                                </button>
                            </div>
                        </TabPane>

                        <TabPane tabId="high" className="py-2 ps-2">
                            {renderList(highItems)}
                            <div className="my-3 text-center">
                                <button type="button" className="btn btn-soft-success waves-effect waves-light" onClick={handleViewAll}>
                                    {st('xem_tat_ca_thong_bao_button')} <i className="ri-arrow-right-line align-middle"></i>
                                </button>
                            </div>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;
